import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import { EntityTypeSupportingGroups, Group, MapEntity } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { CategorizedMapEntries } from "@mapmycustomers/shared/types/map";
import User from "@mapmycustomers/shared/types/User";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";

import DataViewEntity from "@app/component/DataView/type/DataViewEntity";
import { MapFetcherPayload } from "@app/types/map/MapFetcher";
import MapViewportState from "@app/types/map/MapViewportState";

export const enterGroupMode = createAction("map/group/enter")<{
  entityType: EntityTypeSupportingGroups;
  group?: Group; // useful to specify if you do have group already, helps UI to update faster
  groupId: Group["id"];
}>();

export const exitGroupMode = createAsyncAction(
  "map/group/exit/request",
  "map/group/exit/success",
  "map/group/exit/failure"
)<true | void, void, void>(); // arg for request is whether we should forceExit or not

export const initializeGroupsMode = createAsyncAction(
  "map/group/initialize/request",
  "map/group/initialize/success",
  "map/group/initialize/failure"
)<
  {
    callback?: (bounds: google.maps.LatLngBounds) => void;
    entityType: EntityTypeSupportingGroups;
    groupId: Group["id"];
    viewport: MapViewportState;
  },
  {
    entityType: EntityTypeSupportingGroups;
    group: Group;
  },
  void
>();

export const applyGroupFilters = createAction("map/group/applyGroupFilters")<void>();

export const applyGroupsMapViewSettings = createAction("map/group/applyMapViewSettings")<
  Partial<MapViewState>
>();

export const fetchGroupPins = createAsyncAction(
  "map/group/fetchPins/request",
  "map/group/fetchPins/success",
  "map/group/fetchPins/failure"
)<
  MapFetcherPayload,
  CategorizedMapEntries & {
    pinsCount: number;
  },
  unknown
>();

export const fetchGroupOtherPins = createAsyncAction(
  "map/group/fetchGroupOtherPins/request",
  "map/group/fetchGroupOtherPins/success",
  "map/group/fetchGroupOtherPins/failure"
)<unknown, CategorizedMapEntries, unknown>();

export const fetchGroupRecords = createAsyncAction(
  "map/group/fetchRecords/request",
  "map/group/fetchRecords/success",
  "map/group/fetchRecords/failure"
)<
  Partial<Pick<MapViewState, "range" | "search" | "sort">>,
  {
    records: MapEntity[];
    recordsCount: number;
  },
  unknown
>();

export const fetchGroupLassoSelection = createAsyncAction(
  "map/group/fetchGroupLassoSelection/request",
  "map/group/fetchGroupLassoSelection/success",
  "map/group/fetchGroupLassoSelection/failure"
)<
  MapFetcherPayload,
  {
    records: MapEntity[];
  },
  unknown
>();
export const exitGroupLassoMode = createAction("map/group/exitGroupLassoMode")<void>();

export const updateGroup = createAsyncAction(
  "map/group/updateGroup/request",
  "map/group/updateGroup/success",
  "map/group/updateGroup/failure"
)<
  {
    group: Group;
    noNotification?: boolean;
    onSuccess?: (updatedGroup: Group) => void;
  },
  Group,
  void
>();

export const fetchGroup = createAsyncAction(
  "map/group/fetchGroup/request",
  "map/group/fetchGroup/success",
  "map/group/fetchGroup/failure"
)<
  {
    entityType: EntityTypeSupportingGroups;
    groupId: Group["id"];
  },
  Group,
  void
>();

export const updateGroupSharing = createAsyncAction(
  "map/group/updateGroupSharing/request",
  "map/group/updateGroupSharing/success",
  "map/group/updateGroupSharing/failure"
)<
  {
    group: Group;
    onSuccess?: (updatedGroup: Group) => void;
    userIdsToShareWith: User["id"][];
  },
  Group,
  void
>();

export const bulkAddPinsToGroup = createAsyncAction(
  "map/group/bulkAddPinsToGroup/request",
  "map/group/bulkAddPinsToGroup/success",
  "map/group/bulkAddPinsToGroup/failure"
)<
  {
    entities: Array<MapEntity>;
  },
  unknown,
  unknown
>();

export const bulkRemovePinsFromGroup = createAsyncAction(
  "map/group/bulkRemovePinsFromGroup/request",
  "map/group/bulkRemovePinsFromGroup/success",
  "map/group/bulkRemovePinsFromGroup/failure"
)<
  {
    entities: Array<MapEntity>;
  },
  unknown,
  unknown
>();

export const setGroupShowOtherPins = createAction("map/group/setGroupShowOtherPins")<{
  showOtherPins: boolean;
}>();

export const setGroupEditFormHasChanges = createAction(
  "map/group/setEditFormHasChanges"
)<boolean>();

export const fetchDataViewEntities = createAsyncAction(
  "map/group/fetchDataViewEntities/request",
  "map/group/fetchDataViewEntities/success",
  "map/group/fetchDataViewEntities/failure"
)<void, DataViewEntity[], unknown>();

export const downloadGroupRecords = createAction("map/group/downloadGroupRecords")<
  ColumnModel<IField>
>();

export const setGroupRecords = createAction("map/group/setGroupRecords")<MapEntity[]>();

export type GroupModeActions = ActionType<
  | typeof applyGroupFilters
  | typeof applyGroupsMapViewSettings
  | typeof bulkAddPinsToGroup
  | typeof bulkRemovePinsFromGroup
  | typeof downloadGroupRecords
  | typeof enterGroupMode
  | typeof exitGroupLassoMode
  | typeof exitGroupMode
  | typeof fetchDataViewEntities
  | typeof fetchGroup
  | typeof fetchGroupLassoSelection
  | typeof fetchGroupOtherPins
  | typeof fetchGroupPins
  | typeof fetchGroupRecords
  | typeof initializeGroupsMode
  | typeof setGroupEditFormHasChanges
  | typeof setGroupRecords
  | typeof setGroupShowOtherPins
  | typeof updateGroup
  | typeof updateGroupSharing
>;
